// utils
import axios from "src/utils/axios";
import apiSlice from "src/utils/api-slice";

// ----------------------------------------------------------------------

const API_ENDPOINTS = {
    getOrders: "/order/search_orders",
    getSubOrders: "/order/sub_orders",
    //
    syncOrder: "/order/sync",
    importOrder: "/order/import",
    getImportOrderPreSignedURL: "/order/order_import_pre_signed_url",
    importOrderV2: "/order/import/v2",
    createOrder: "/order/create_onsite",
    deleteOrder: "/order/delete",
    getOrderDetail: "/order/order_detail",
    //
    changeToAwaiting: "/order/change_to_awaiting",
    changeToCancel: "/order/change_to_cancel",
    changeToShipped: "/order/change_to_shipped",
    //
    splitOrder: "/order/split_order",
    splitOrderRevert: "/order/revert_split_order",
    //
    getAvailableCombineOrders: "/order/list_combine_candidate",
    combineOrder: "/order/combine_order",
    combineOrderRevert: "/order/revert_combine_order",
    //
    getTagList: "/order/tag/query_tags",
    createTagList: "/order/tag/create",
    addTag: "/order/add_tags",
    removeTag: "/order/remove_tags",
    //
    modifyOrdersPackage: "/order/batch_edit_order_package",
    //
    getPackingSlip: "/order/download_packing_slip",
    getPackingSlips: "/order/batch_download_packing_slip",
    //
    getCustomPrint: "order/custom_print",
    updateOrderPackage: "/order/edit_order_package",
    getOrderRateMode: "/automation/config/order_get_rate_view_setting/get",
    setOrderRateMode: "/automation/config/order_get_rate_view_setting/save",
    buyLabel: "/order/buy_label_from_order",
    printLabel: "/order/print_label",
    sendTrackingEmail: "/order/notify/send"
};

// ----------------------------------------------------------------------

const getOrders = (request) => axios.get(`${ API_ENDPOINTS.getOrders }`, {
    params: {
        ...request
    }
});

const getSubOrders = (id) => axios.get(`${ API_ENDPOINTS.getSubOrders }`, {
    params: {
        parent_order_id: id
    }
});

const syncOrder = (request) => axios.post(`${ API_ENDPOINTS.syncOrder }`, { ...request });

const getImportOrderPreSignedURL = () => axios.get(`${ API_ENDPOINTS.getImportOrderPreSignedURL }`);

const importOrder = (request) => axios.post(`${ API_ENDPOINTS.importOrderV2 }`, { ...request });

const createOrder = (request) => axios.post(`${ API_ENDPOINTS.createOrder }`, { ...request });

const deleteOrder = (id) => axios.post(`${ API_ENDPOINTS.deleteOrder }`, { order_id: id });

const getOrderDetail = (id) => axios.get(`${ API_ENDPOINTS.getOrderDetail }`, {
    params: {
        order_id: id
    }
});

const updateOrderStatus = (toStatus, id) =>
    // eslint-disable-next-line no-nested-ternary
    toStatus === "awaiting" ? axios.post(`${ API_ENDPOINTS.changeToAwaiting }`, { order_id: id }) :
        // eslint-disable-next-line no-nested-ternary
        toStatus === "canceled" ? axios.post(`${ API_ENDPOINTS.changeToCancel }`, { order_id: id }) :
            toStatus === "shipped" ? axios.post(`${ API_ENDPOINTS.changeToShipped }`, { order_id: id }) :
                null
;

const splitOrder = (request) => axios.post(`${ API_ENDPOINTS.splitOrder }`, { ...request });

const splitOrderRevert = (id) => axios.post(`${ API_ENDPOINTS.splitOrderRevert }`, { order_id: id });

const getAvailableCombineOrders = () => axios.get(`${ API_ENDPOINTS.getAvailableCombineOrders }`);

const combineOrder = (request) => axios.post(`${ API_ENDPOINTS.combineOrder }`, { ...request });

const combineOrderRevert = (id) => axios.post(`${ API_ENDPOINTS.combineOrderRevert }`, { order_id: id });

const createTagList = (request) => axios.post(`${ API_ENDPOINTS.createTagList }`, { ...request });

const addTag = (request) => axios.post(`${ API_ENDPOINTS.addTag }`, null, {
    params: {
        ...( request?.order_id && { order_id: request?.order_id } ),
        ...( request?.order_ids && { order_ids: ( request?.order_ids ?? [] ).join(",") } ),
        tag_ids: ( request?.tag_ids ?? [] ).join(","),
        modify_type: request?.modify_type ?? "COVER"
    }
});

const removeTag = (request) => axios.post(`${ API_ENDPOINTS.removeTag }`, null, {
    params: {
        order_id: request?.order_id,
        tag_ids: ( request?.tag_ids ?? [] ).join(",")
    }
});

const modifyOrdersPackage = (request) => axios.post(`${ API_ENDPOINTS.modifyOrdersPackage }`, { ...request });

const getPackingSlip = (request) => axios.post(`${ API_ENDPOINTS.getPackingSlip }`, { ...request }, { responseType: "blob" });

const getPackingSlips = (request) => axios.post(`${ API_ENDPOINTS.getPackingSlips }`, { ...request });

const getOrderCustomPrint = (id) => axios.get(`${ API_ENDPOINTS.getCustomPrint }`, {
    params: {
        order_id: id
    }
});

const updateOrderPackage = (request) => axios.post(`${ API_ENDPOINTS.updateOrderPackage }`, { ...request });

const getOrderRateMode = () => axios.get(`${ API_ENDPOINTS.getOrderRateMode }`);

const setOrderRateMode = (request) => axios.post(`${ API_ENDPOINTS.setOrderRateMode }`, { ...request });

const buyLabel = (request, fingerprint) => axios.post(`${ API_ENDPOINTS.buyLabel }`, { ...request }, {
    headers: {
        "X-Fingerprint-Request-Id": fingerprint?.requestId ?? ""
    }
});

const printLabel = (id) => axios.post(`${ API_ENDPOINTS.printLabel }`, { order_id: id }, { responseType: "blob" });

const sendTrackingEmail = (id) => axios.post(`${ API_ENDPOINTS.sendTrackingEmail }`, { shipment_no: id });

// ----------------------------------------------------------------------

const {
    useGetTagListQuery
} = apiSlice.injectEndpoints({
    endpoints: builder => ( {
        getTagList: builder.query({
            keepUnusedDataFor: 5,
            providesTags: [ "TagList" ],
            query: () => `${ API_ENDPOINTS.getTagList }`,
            transformResponse: ({ data }, meta, arg) => data
        })
    } ),
    overrideExisting: true
});

// ----------------------------------------------------------------------

export {
    useGetTagListQuery,
    //
    getOrders as getOrdersAPI,
    getSubOrders as getSubOrdersAPI,
    //
    syncOrder as syncOrderAPI,
    getImportOrderPreSignedURL as getImportOrderPreSignedURLAPI,
    importOrder as importOrderAPI,
    createOrder as createOrderAPI,
    deleteOrder as deleteOrderAPI,
    getOrderDetail as getOrderDetailAPI,
    //
    updateOrderStatus as updateOrderStatusAPI,
    //
    splitOrder as splitOrderAPI,
    splitOrderRevert as splitOrderRevertAPI,
    //
    getAvailableCombineOrders as getAvailableCombineOrdersAPI,
    combineOrder as combineOrderAPI,
    combineOrderRevert as combineOrderRevertAPI,
    //
    createTagList as createTagListAPI,
    addTag as addTagAPI,
    removeTag as removeTagAPI,
    //
    modifyOrdersPackage as modifyOrdersPackageAPI,
    //
    getPackingSlip as getPackingSlipAPI,
    getPackingSlips as getPackingSlipsAPI,
    // Buy Label Related
    getOrderCustomPrint as getOrderCustomPrintAPI,
    updateOrderPackage as updateOrderPackageAPI,
    getOrderRateMode as getOrderRateModeAPI,
    setOrderRateMode as setOrderRateModeAPI,
    buyLabel as buyLabelOrderAPI,
    printLabel as printLabelOrderAPI,
    sendTrackingEmail as sendTrackingEmailAPI
};
